// Hoja semanal de horas (D96): la semana de lunes a domingo en hora de Bogota, una fila por proyecto (y fase), caso o
// "Interno" (sin proyecto ni caso), y lo que escribe la persona en cada celda (1:30, 1.5, 90m). Funciones puras.
import type { RegistroHoras } from '../api/proyectos';

/** Meta semanal por persona, en minutos. */
export const META_SEMANA = 40 * 60;
const BOGOTA = -5 * 3_600_000; // Colombia no cambia de hora
const DIA = 86_400_000;

/** "2026-10-28": el dia de una fecha en hora de Bogota. */
export const diaDe = (fecha: string | number | Date) => new Date(new Date(fecha).getTime() + BOGOTA).toISOString().slice(0, 10);

/** La semana (lunes a domingo, hora de Bogota) que contiene esa fecha: limites y los 7 dias. */
export function semanaDe(fecha: string | number | Date = Date.now()) {
  const t = new Date(fecha).getTime() + BOGOTA;
  const d = new Date(t);
  const lunes = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  const dias = Array.from({ length: 7 }, (_, i) => new Date(lunes + i * DIA).toISOString().slice(0, 10));
  return { desde: new Date(lunes - BOGOTA), hasta: new Date(lunes + 7 * DIA - BOGOTA), dias };
}

/** Mediodia de Bogota de un dia "2026-10-28" (asi se guardan las fechas de las horas). */
export const mediodiaDe = (dia: string) => new Date(Date.parse(`${dia}T12:00:00Z`) - BOGOTA).toISOString();

/**
 * Lo que escribe la persona en una celda, en minutos: "1:30" o "1.5" o "1,5" (horas), "90m", "2h", "2" (horas).
 * Vacio es 0. null si no se entiende.
 */
export function parsearDuracion(texto: string): number | null {
  const t = texto.trim().toLowerCase().replace(/\s+/g, '');
  if (!t) return 0;
  let m: RegExpMatchArray | null;
  if ((m = t.match(/^(\d{1,2}):([0-5]?\d)$/))) return Number(m[1]) * 60 + Number(m[2]);
  if ((m = t.match(/^(\d+)m(in)?$/))) return Number(m[1]);
  if ((m = t.match(/^(\d+(?:[.,]\d+)?)h?$/))) {
    const h = Number(m[1].replace(',', '.'));
    return h > 24 ? null : Math.round(h * 60);
  }
  if ((m = t.match(/^(\d+)h(\d{1,2})m?$/))) return Number(m[1]) * 60 + Number(m[2]);
  return null;
}

/** "1:30", "2", "0:20" para la hoja. */
export const duracionTexto = (min: number) => {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return m ? `${h}:${String(m).padStart(2, '0')}` : String(h);
};

export type TipoFila = 'proyecto' | 'caso' | 'interno';
/** Clave de una fila: p:<proyecto>:<fase> · c:<caso> · i */
export const claveDe = (r: Pick<RegistroHoras, 'proyectoId' | 'casoId' | 'faseId'>) => (r.casoId ? `c:${r.casoId}` : r.proyectoId ? `p:${r.proyectoId}:${r.faseId ?? ''}` : 'i');
export function partesDe(clave: string): { tipo: TipoFila; proyectoId: string | null; faseId: string | null; casoId: string | null } {
  const [t, a, b] = clave.split(':');
  if (t === 'c') return { tipo: 'caso', proyectoId: null, faseId: null, casoId: a ?? null };
  if (t === 'p') return { tipo: 'proyecto', proyectoId: a ?? null, faseId: b || null, casoId: null };
  return { tipo: 'interno', proyectoId: null, faseId: null, casoId: null };
}

export type FilaHoja = ReturnType<typeof partesDe> & { clave: string; celdas: Record<string, RegistroHoras[]>; total: number };

/** Filas de la hoja: lo registrado en la semana mas las filas que la persona agrego (vacias). Proyectos, casos, interno. */
export function filasHoja(regs: RegistroHoras[], dias: string[], extra: string[] = []): FilaHoja[] {
  const filas = new Map<string, FilaHoja>();
  const fila = (clave: string) => {
    let f = filas.get(clave);
    if (!f) filas.set(clave, (f = { ...partesDe(clave), clave, celdas: Object.fromEntries(dias.map((d) => [d, []])), total: 0 }));
    return f;
  };
  for (const r of regs) {
    const d = diaDe(r.fecha);
    if (!dias.includes(d)) continue;
    const f = fila(claveDe(r));
    f.celdas[d].push(r);
    f.total += r.minutos;
  }
  for (const c of extra) fila(c);
  const orden: Record<TipoFila, number> = { proyecto: 0, caso: 1, interno: 2 };
  return [...filas.values()].sort((a, b) => orden[a.tipo] - orden[b.tipo] || a.clave.localeCompare(b.clave));
}

export const minutosDe = (rs: RegistroHoras[]) => rs.reduce((s, r) => s + r.minutos, 0);

export function totalesHoja(filas: FilaHoja[], dias: string[]) {
  const porDia = Object.fromEntries(dias.map((d) => [d, filas.reduce((s, f) => s + minutosDe(f.celdas[d] ?? []), 0)]));
  return { porDia, total: Object.values(porDia).reduce((s, x) => s + x, 0) };
}

export type FilaEquipo = { id: string; nombre: string; porDia: Record<string, number>; total: number; facturable: number; pendientes: RegistroHoras[]; rechazadas: number; meta: number };
/** Horas del equipo en la semana: una fila por persona (tambien las que no registraron nada). */
export function resumenEquipo(regs: RegistroHoras[], dias: string[], personas: { id: string; nombre: string }[]): FilaEquipo[] {
  return personas
    .map((p) => {
      const suyas = regs.filter((r) => r.miembroId === p.id && dias.includes(diaDe(r.fecha)));
      const porDia = Object.fromEntries(dias.map((d) => [d, minutosDe(suyas.filter((r) => diaDe(r.fecha) === d))]));
      const total = minutosDe(suyas);
      return {
        id: p.id,
        nombre: p.nombre,
        porDia,
        total,
        facturable: minutosDe(suyas.filter((r) => r.facturable)),
        pendientes: suyas.filter((r) => r.aprobacion === 'PENDIENTE'),
        rechazadas: suyas.filter((r) => r.aprobacion === 'RECHAZADA').length,
        meta: Math.round((100 * total) / META_SEMANA),
      };
    })
    .sort((a, b) => b.pendientes.length - a.pendientes.length || a.total - b.total);
}
